import * as d3 from "d3";
import _, { random } from "lodash";
import $ from 'jquery';
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Divider, Dropdown, Header, Icon, Input, Label, Segment } from "semantic-ui-react";
import { restRequest } from "../../../http";
import { PlotChannelColors } from "../../../models/common_model";
import { MeasurementPlot, LimitData, MeasurementPlotInfo, DATA_TYPE_SCALAR, DATA_TYPE_ARRAY } from "../../../models/testing_model";
import { generateParametersKey, untilStable } from "../../../utils";
import { TestingVisualSearchParameterContext } from "../../../context";

const statusColors = ['', PlotChannelColors[0], 'darkred'];
const statisticsColors = ['green', 'purple'];

export function ScatterPlot(props: { testing_id: string, measurementKey: string, dataType: number, info?: MeasurementPlotInfo, requestParams?: { status?: number, serial_number?: string, start_time?: string, end_time?: string, testing_station_id?: string, employee_id?: string }, onLoading: (status: boolean) => void }) {
    const { testing_id, measurementKey, dataType, info, requestParams } = props;
    const [searchParameters, setParentSearchParameter] = useContext(TestingVisualSearchParameterContext)!;
    const sMeasurementReadyKey = useState('');
    const sScatterData = useState<MeasurementPlot[]>([]);
    const sFilteredData = useState<MeasurementPlot[]>();
    const sSelectedMouseY = useState<number>(0);
    const sSelectedData = useState<MeasurementPlot[]>([]);
    const sForSelectData = useState<MeasurementPlot[]>([]);
    const sForSelectSelectedData = useState<MeasurementPlot[]>([]);
    const sSearchedData = useState<MeasurementPlot[]>([]);
    const sSerialNumberKeyword = useState<string>(_.get(searchParameters, 't_4_2_serial_keyword', ''));
    const sChannelIndex = useState(parseInt(_.get(searchParameters, 't_4_2_channel', '1') || '1'));
    const sParameterIndex = useState(parseInt(_.get(searchParameters, 't_4_2_parameter_index', '0') || '0'));
    const svgRootRef = useRef<HTMLDivElement>(null);
    const toolTipRef = useRef<HTMLDivElement>(null);
    const sStatus = useState([true, true]);
    const sAverage = useState(_.get(searchParameters, 't_4_2_average', '0') === '1');
    const sMedian = useState(_.get(searchParameters, 't_4_2_median', '0') === '1');
    const sStatisticsData = useState<{color: string, value: number}[]>([]);
    const sFullSize = useState<number[]>();
    const canvasContextRef = useRef<CanvasRenderingContext2D>();
    const operationContextRef = useRef<CanvasRenderingContext2D>();
    const originalXRef = useRef<d3.ScaleLinear<number, number, never>>();
    const originalYRef = useRef<d3.ScaleLinear<number, number, never>>();
    const currentAxisRef = useRef<{
        x: d3.ScaleLinear<number, number, never>,
        y: d3.ScaleLinear<number, number, never>,
        xMiddle: number
    }>();
    const limitRef = useRef<LimitData<number>>();
    const sChangingAxis = useState(0);
    const sIsDataPreparing = useState(true);
    const sIsCanvasPreparing = useState(true);
    const TOOLBAR_WIDTH = 250;
    const bisectDate = d3.bisector<number, number>(function (d) { return d }).left;
    const margin = ({ top: 20, right: 30, bottom: 30, left: 50 });

    useEffect(() => {
        setParentSearchParameter((ps: any) => {
            const data = {tab_path: 't_4_2'} as any;
            data.t_4_2_serial_keyword = sSerialNumberKeyword[0] || '';
            data.t_4_2_channel = sChannelIndex[0];
            data.t_4_2_parameter_index = sParameterIndex[0];
            data.t_4_2_average = sAverage[0] ? '1' : '0';
            data.t_4_2_median = sMedian[0] ? '1' : '0';

            return {...ps, ...data};
        });
    }, [sSerialNumberKeyword[0], sChannelIndex[0], sParameterIndex[0], sAverage[0], sMedian[0]]);

    useEffect(() => {
        const kw = sSerialNumberKeyword[0];
        if (!kw || kw.length < 3) {
            sSearchedData[1]([]);
            return;
        }

        const filteredData: [number, MeasurementPlot][] = []
        for (const d of sScatterData[0]) {
            const i = d.serial_number.indexOf(kw);
            if (i !== -1) {
                filteredData.push([i, d]);
            }
        }

        sSearchedData[1](filteredData.sort((a, b) => a[0] > b[0] ? 1 : (a[0] == b[0] ? 0 : -1)).map(v => v[1]).slice(0, 10));
    }, [sScatterData[0], sSerialNumberKeyword[0]]);


    useEffect(() => {
        props.onLoading(true);
        const parameters = { ...requestParams, measurement_key: measurementKey, channel: sChannelIndex[0], parameter: sParameterIndex[0] };
        restRequest<MeasurementPlot[]>(`testings/overview_plot_measurement/${testing_id}`, parameters, 'post', undefined, `overview_plot_measurement/${testing_id}/${generateParametersKey(parameters)}`).then(result => {
            if (dataType == DATA_TYPE_SCALAR) {
                result.filter(r => {
                    let v;
                    try {
                        v = JSON.parse(r.result);
                    } catch (e) {
                        return false;
                    }
                    if (!_.isNumber(v)) {
                        return false;
                    }
                    r.vibrateX = 0.5 - Math.random();
                    r.result = [v];
                    return true;
                });
            } else {
                result.forEach(r => {
                    let v;
                    try {
                        v = JSON.parse(r.result);
                    } catch (e) {
                        return false;
                    }
                    if (!_.isArray(v) || !_.isNumber(v[0])) {
                        return false;
                    }
                    r.result = v;
                    r.vibrateX = 1 - Math.random() * 2;
                    return true;
                });
            }

            result = result.filter(r => _.isNumber(r.result[0]))

            sScatterData[1](result);
            sMeasurementReadyKey[1](measurementKey);
        }).finally(() => {
            props.onLoading(false);
        });
    }, [testing_id, measurementKey, requestParams, sChannelIndex[0], sParameterIndex[0]]);

    useEffect(() => {
        const parameterIndex = sParameterIndex[0];
        const all_values: number[] = []

        sFilteredData[1](sScatterData[0].filter(v => {
            if (sStatus[0][v.status - 1]) {
                return true;
            }

            return false;
        }).map(r => {
            r.value = r.result[parameterIndex];
            all_values.push(r.value!);
            return r;
        }));

        const statisticsData = []
        if (sAverage[0]) {
            statisticsData.push({
                color: statisticsColors[0],
                value: d3.mean(all_values)!
            })
        }
        if (sMedian[0]) {
            statisticsData.push({
                color: statisticsColors[1],
                value: d3.median(all_values)!
            })
        }

        sStatisticsData[1](statisticsData);
    }, [sScatterData[0], sStatus[0], sParameterIndex[0], sAverage[0], sMedian[0]]);

    useEffect(() => {
        sIsDataPreparing[1](true);

        return untilStable<number>(() => svgRootRef.current!.offsetWidth, (svgWidth) => {
            if (!sFilteredData[0] || !info || sMeasurementReadyKey[0] !== measurementKey) {
                return;
            }

            const fullWidth = svgWidth - TOOLBAR_WIDTH;
            const totalHeight = window.innerHeight - $(svgRootRef.current!).offset()!.top;
            const fullHeight = Math.min(fullWidth * 0.618, totalHeight);

            const width = fullWidth - margin.left - margin.right;
            const height = fullHeight - margin.top - margin.bottom;
            const parameterIndex = sParameterIndex[0];
            const xDomain = [parameterIndex - 2, parameterIndex + 4];
            const x = d3.scaleLinear().domain(xDomain).range([margin.left, width - margin.right]);
            const xMiddle = parameterIndex + 1;

            const resultValues = sFilteredData[0]!;
            const allValues = resultValues.map(v => v.value!);
            let limitData: LimitData<number[]>[];
            if (dataType == DATA_TYPE_SCALAR) {
                limitData = info?.limits as LimitData<number[]>[];

                limitData.forEach(l => {
                    if (!_.isArray(l.lower)) {
                        l.lower = [l.lower as any]
                    }
                    if (!_.isArray(l.upper)) {
                        l.upper = [l.upper as any]
                    }
                })
            } else {
                limitData = info?.limits as LimitData<number[]>[];
            }

            const limit = limitData[sChannelIndex[0] - 1];
            const scalarLimit: LimitData<number> = {};
            if (limit) {
                if (limit.lower && _.isNumber(limit.lower[parameterIndex])) {
                    const lower = limit.lower[parameterIndex];
                    scalarLimit.lower = lower;
                    allValues.push(lower);
                }
                if (limit.upper && _.isNumber(limit.upper[parameterIndex])) {
                    const upper = limit.upper[parameterIndex];
                    scalarLimit.upper = upper;
                    allValues.push(upper);
                }
            }

            const yDomain = d3.extent(allValues) as number[];
            const y = d3.scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top]);

            originalXRef.current = x;
            originalYRef.current = y;

            currentAxisRef.current = {
                x,
                y,
                xMiddle: xMiddle
            }

            limitRef.current = scalarLimit;
            sFullSize[1]([fullWidth, fullHeight]);
            sIsDataPreparing[1](false);
        });
    }, [sFilteredData[0], info, sParameterIndex[0], sMeasurementReadyKey[0], measurementKey]);

    useEffect(() => {
        if (sIsDataPreparing[0]) {
            return;
        }

        sIsCanvasPreparing[1](true);

        const [fullWidth, fullHeight] = sFullSize[0]!;
        let canvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.base');
        let operationCanvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.operation');

        if (canvas.size() === 0) {
            canvas = d3.select(svgRootRef.current!)
                .append("canvas")
                .attr('class', 'base');

            operationCanvas = d3.select(svgRootRef.current!)
                .append("canvas")
                .attr('class', 'operation')
                .style('background', 'rgba(255,255,255,0)')
                .style('position', 'absolute')
                .style('z-index', '1')
                .style('top', '0')
                .style('left', '0');
        }

        canvas.attr("width", fullWidth).attr("height", fullHeight);
        operationCanvas.attr("width", fullWidth).attr("height", fullHeight);
        canvasContextRef.current = canvas.node()!.getContext('2d')!;
        operationContextRef.current = operationCanvas.node()!.getContext('2d')!;

        sForSelectData[1]([]);

        sIsCanvasPreparing[1](false);
    }, [sFilteredData[0], sIsDataPreparing[0]]);

    useEffect(() => {
        if (sIsCanvasPreparing[0]) {
            return;
        }

        let isMounted = true;

        let operationCanvas = d3.select(svgRootRef.current!).select<HTMLCanvasElement>('canvas.operation');
        let currentSelectedData: MeasurementPlot[] = [];

        const [fullWidth, fullHeight] = sFullSize[0]!;

        const currentX = currentAxisRef.current?.x;
        const currentY = currentAxisRef.current?.y;
        let miniDiff = currentX && currentX.domain() ? (Math.abs(currentX.invert(2)-currentX.invert(0)) + Math.abs(currentY!.invert(2) - currentY!.invert(0))) : 5;

        let isZooming = false;

        d3.select(document)
            .on('mousedown', function () {
                isZooming = true;
            }).on('mouseup', function () {
                isZooming = false;
            })

        operationCanvas.on('mouseover', function () {
        })
            .on('mouseout', function () {
            })
            .on('mousemove', function (event) {
                if (!isMounted || isZooming || event.button == 1) {
                    return;
                }

                if (sFilteredData[0]) {
                    const mouse = d3.pointer(event);
                    const mouseX = currentAxisRef.current!.x.invert(mouse[0] - margin.left);
                    const mouseY = currentAxisRef.current!.y.invert(mouse[1] - margin.top);

                    const selectedUnits: [number, MeasurementPlot][] = [];
                    const allValues = sFilteredData[0].map(d => d.value!)
                    let i = bisectDate(allValues, mouseY);

                    if (i < allValues.length) {
                        if (i > 0 && mouseY - allValues[i - 1] < allValues[i] - mouseY) {
                            i -= 1;
                        }
                    }

                    const xMiddle = currentAxisRef.current!.xMiddle;

                    for (const d of sFilteredData[0]) {
                        const diff = Math.abs(xMiddle + d.vibrateX! - mouseX) + Math.abs(d.value! - mouseY);
                        if (diff < miniDiff) {
                            selectedUnits.push([diff, d])
                        }
                    }

                    const selectedVectorData = selectedUnits.sort((a, b) => a[0] > b[0] ? 1 : (a[0] === b[0] ? 0 : -1)).slice(0, 10).map(s => s[1]);
                    sSelectedMouseY[1](mouseY);
                    sSelectedData[1](selectedVectorData);

                    currentSelectedData = selectedVectorData;
                }
            }).on('click', function () {
                sForSelectData[1](currentSelectedData);
                sForSelectSelectedData[1]([]);
            });


        const width = fullWidth - margin.left - margin.right;
        const height = fullHeight - margin.top - margin.bottom;
        const zoom = d3.zoom()
            .scaleExtent([0.1, 10000])
            .translateExtent([[-width, -height], [width * 2, height * 2]])
            .on("zoom", (e: d3.D3ZoomEvent<any, any>) => {
                if (!isMounted) {
                    return;
                }
                currentAxisRef.current!.x = e.transform.rescaleX(originalXRef.current!);
                currentAxisRef.current!.y = e.transform.rescaleY(originalYRef.current!);
                sChangingAxis[1](s => s + 1);

                const currentX = currentAxisRef.current?.x;
                const currentY = currentAxisRef.current?.y;

                miniDiff = currentX && currentX.domain() ? (Math.abs(currentX.invert(2)-currentX.invert(0)) + Math.abs(currentY!.invert(2) - currentY!.invert(0))) : 5;
            });

        operationCanvas.call(zoom as any);

        operationCanvas.on("dblclick.zoom", () => {
            if (!isMounted) {
                return;
            }

            currentAxisRef.current!.x = originalXRef.current!;
            currentAxisRef.current!.y = originalYRef.current!;
            operationCanvas.call(zoom.transform as any, d3.zoomIdentity.scale(1));
            operationCanvas.call(zoom.transform as any, d3.zoomIdentity.translate(0, 0));
            sChangingAxis[1](s => s + 1);

            const currentX = currentAxisRef.current?.x;
            const currentY = currentAxisRef.current?.y;

            miniDiff = currentX && currentX.domain() ? (Math.abs(currentX.invert(2)-currentX.invert(0)) + Math.abs(currentY!.invert(2) - currentY!.invert(0))) : 5;
        });

        return () => {
            isMounted = false;
        }
    }, [sIsCanvasPreparing[0], sFullSize[0]]);

    useEffect(() => {
        if (sIsDataPreparing[0] || sIsCanvasPreparing[0]) {
            return;
        }

        const currentAxis = currentAxisRef.current!;
        const operationContext = operationContextRef.current;
        if (operationContext && sFullSize[0]) {
            operationContext.clearRect(0, 0, sFullSize[0][0], sFullSize[0][1]);
            operationContext.save();
            operationContext.translate(margin.left, margin.top);
            const currentY = currentAxis.y;
            for (const d of _.uniq(sSelectedData[0].concat(sForSelectSelectedData[0]))) {
                operationContext.beginPath();

                operationContext.arc(currentAxis.x(currentAxis.xMiddle + d.vibrateX!), currentY(d.value!), 3, 0, 2 * Math.PI, false);
                operationContext.strokeStyle = 'yellow';
                operationContext.lineWidth = 1;

                operationContext.stroke();
            }

            for(const v of sStatisticsData[0]) {
                operationContext.beginPath();
                const yValue = currentAxis.y(v.value);
                operationContext.moveTo(margin.left, yValue);
                operationContext.lineTo(sFullSize[0][0] - margin.right, yValue);
                operationContext.strokeStyle = v.color;
                operationContext.lineWidth = 1;
                operationContext.fillStyle = '';
                operationContext.stroke();
            }

            operationContext.restore();
        }
    }, [sSelectedData[0], sForSelectSelectedData[0], sIsDataPreparing[0], sIsCanvasPreparing[0], sChangingAxis[0]]);

    useEffect(() => {
        if (sIsDataPreparing[0] || sIsCanvasPreparing[0]) {
            return;
        }

        const [fullWidth, fullHeight] = sFullSize[0]!;
        const context = canvasContextRef.current!;
        context.clearRect(0, 0, fullWidth, fullHeight);

        const width = fullWidth - margin.left - margin.right;
        const height = fullHeight - margin.top - margin.bottom;

        const parameterIndex = sParameterIndex[0];

        const currentAxis = currentAxisRef.current!;

        const x = currentAxis.x;
        const y = currentAxis.y;

        const xDomain = x.domain();
        const xMiddle = sParameterIndex[0] + 1;

        const xAxis = () => {
            const tickCount = 5;
            const tickSize = 6;
            const ticks = x.ticks(tickCount);
            const tickFormat = x.tickFormat(tickCount, 'd');

            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(x(d), height);
                context.lineTo(x(d), 0);
            });
            context.strokeStyle = "gray";
            context.stroke();

            const xAxisTitle = xDomain[xDomain.length - 1] >= 1500 ? 'Parameter ' + xMiddle : '';
            context.beginPath();
            context.strokeStyle = "black";
            const metrics = context.measureText(xAxisTitle);
            const textWidth = metrics.width;
            context.fillText(xAxisTitle, x(xDomain[0]) + x(xDomain[1]) / 2 - x(xDomain[0]) / 2 - textWidth / 2, fullHeight - 20);

            context.beginPath();
            context.lineTo(0, height - tickSize);
            context.lineTo(width, height - tickSize);
            context.strokeStyle = "gray";
            context.stroke();

            context.textAlign = "center";
            context.textBaseline = "top";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), x(d), height + tickSize);
            });
        }

        const yAxis = () => {
            const tickCount = 10,
                tickSize = 6,
                tickPadding = 3,
                ticks = y.ticks(tickCount),
                tickFormat = y.tickFormat(tickCount);

            context.beginPath();
            ticks.forEach(function (d) {
                context.moveTo(0, y(d));
                context.lineTo(width, y(d));
            });
            context.strokeStyle = "black";
            context.stroke();

            context.beginPath();
            context.moveTo(-tickSize, 0);
            context.lineTo(0.5, 0);
            context.lineTo(0.5, height);
            context.lineTo(-tickSize, height);
            context.strokeStyle = "black";
            context.stroke();

            context.textAlign = "right";
            context.textBaseline = "middle";
            ticks.forEach(function (d) {
                context.fillText(tickFormat(d), -tickSize - tickPadding, y(d));
            });

            const yAxisTitle = 'Value';
            context.save();
            context.rotate(-Math.PI / 2);
            context.textAlign = "right";
            context.textBaseline = "top";
            context.strokeStyle = "black";
            const metrics = context.measureText(yAxisTitle);
            const textWidth = metrics.width;
            context.fillText(yAxisTitle, -(height / 2) + textWidth / 2, -margin.left);
            context.restore();
        }


        let tooltipTop = 0
        let currentTooltipElem = svgRootRef.current
        while (currentTooltipElem) {
            tooltipTop += currentTooltipElem.offsetTop;
            currentTooltipElem = currentTooltipElem.offsetParent as any;
        }

        context.save();
        context.translate(margin.left, margin.top);
        xAxis();
        yAxis();

        sFilteredData[0]!.forEach((d, index) => {
            context.beginPath();
            context.arc(currentAxis.x(currentAxis.xMiddle + d.vibrateX!), y(d.value!), 3, 0, 2 * Math.PI, false);
            context.strokeStyle = statusColors[d.status];
            context.lineWidth = 1;
            context.stroke();
        });

        const limit = limitRef.current;
        if (limit) {
            if (_.isNumber(limit.lower)) {
                const lowerY = y(limit.lower);

                context.beginPath();
                context.moveTo(margin.left, lowerY);
                context.lineTo(width - margin.right, lowerY);
                context.strokeStyle = 'red';
                context.lineWidth = 1;
                context.fillStyle = '';
                context.stroke();
            }

            if (_.isNumber(limit.upper)) {
                const upperY = y(limit.upper);
                context.beginPath();
                context.moveTo(margin.left, upperY);
                context.lineTo(width - margin.right, upperY);
                context.strokeStyle = 'red';
                context.lineWidth = 1;
                context.fillStyle = '';
                context.stroke();
            }
        }

        context.restore();
    }, [info, sFilteredData[0], sIsDataPreparing[0], sIsCanvasPreparing[0], sChangingAxis[0]]);

    return <div>
        {info && <div style={{ width: `${TOOLBAR_WIDTH}px`, paddingTop: '50px', float: 'left' }}>
            <Divider></Divider>
            <Button.Group compact>
                {info?.channels.map(c => <Button key={c} secondary basic={sChannelIndex[0] !== c} onClick={e => {
                    sChannelIndex[1](c);
                }}>CH{c}</Button>)}
            </Button.Group>
            <Divider></Divider>

            <Button.Group compact>
                <Button style={{ background: sAverage[0] ? statisticsColors[0] : null, color: '#fff' }} color="grey" onClick={e => {
                    sAverage[1](v => !v);
                }}>Average</Button>
                <Button style={{ background: sMedian[0] ? statisticsColors[1] : null, color: '#fff' }} color="grey" onClick={e => {
                    sMedian[1](v => !v);
                }}>Median</Button>
            </Button.Group>
            <Divider></Divider>


            {dataType == DATA_TYPE_ARRAY && <>
                <Dropdown className="secondary" button options={_.range(info!.parameter_size).map(i => ({ key: i, value: i, text: 'Parameter ' + (i + 1) }))} value={sParameterIndex[0]} onChange={(e, d) => {
                    sParameterIndex[1](d.value as number);
                }}></Dropdown>
                <Divider></Divider>
            </>}

            <Header attached="top" size="tiny">Selected</Header>
            <Segment attached="bottom" size="tiny">
                <div>
                    Y: {sSelectedMouseY[0]}<br />
                </div>

                <Divider></Divider>
                <div className="button_container">
                    {sSelectedData[0].map(d => <Label key={d.file_name} size="tiny" basic style={{ color: statusColors[d.status] }}>{d.serial_number}</Label>)}
                </div>

                <Divider></Divider>
                <div className="button_container">
                    {sForSelectData[0].map(d => <Button style={{ opacity: sForSelectSelectedData[0].includes(d) ? 1 : 0.314, background: statusColors[d.status], color: '#fff' }} onClick={e => {
                        e.preventDefault();
                        if (sForSelectSelectedData[0].includes(d)) {
                            sForSelectSelectedData[1](vs => vs.filter(v => v !== d));
                        } else {
                            sForSelectSelectedData[1](vs => [...vs, d]);
                        }
                    }} onDoubleClick={(e: any) => {
                        e.preventDefault();
                        window.open(`/testing_file_visual/${d.file_id}/3/${measurementKey}`, '_blank');
                    }} key={d.file_name} size="mini" compact title={d.file_name + ' (Double Click for Detail)'}>{d.serial_number}</Button>)}
                </div>
            </Segment>

            <Divider></Divider>

            <Header attached="top">
                <Input icon placeholder='Search By Serial...' size="small">
                    <input value={sSerialNumberKeyword[0]} onChange={e => sSerialNumberKeyword[1](e.target.value)} />
                    <Icon name='search' />
                </Input>
            </Header>
            <Segment attached="bottom" size="tiny">
                <div className="button_container">
                    {sSearchedData[0].map(d => <Button key={d.file_name} onClick={e => {
                        e.preventDefault();

                        let contains = false;
                        if (sForSelectSelectedData[0].includes(d)) {
                            sForSelectSelectedData[1](vs => vs.filter(v => v !== d));
                            contains = true;
                        }

                        if (sSelectedData[0].includes(d)) {
                            sSelectedData[1](vs => vs.filter(v => v !== d));
                            contains = true;
                        }

                        if (!contains) {
                            sForSelectSelectedData[1](vs => [...vs, d]);
                        }

                    }} onDoubleClick={(e: any) => {
                        e.preventDefault();
                        window.open(`/testing_file_visual/${d.file_id}/3/${measurementKey}`, '_blank');
                    }} style={{ opacity: (sForSelectSelectedData[0].includes(d) || sSelectedData[0].includes(d)) ? 1 : 0.314, background: statusColors[d.status], color: '#fff' }} title={d.file_name + ' (Double Click for Detail)'} size="mini" compact>{d.serial_number}</Button>)}
                </div>
            </Segment>
        </div>}
        <div style={{ position: 'relative', width: `calc(100% - ${TOOLBAR_WIDTH}px)`, float: 'left' }} ref={svgRootRef}></div>
    </div>;
}
